import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cm(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function verifyEmail(email: string): boolean {
  const emailRegex =
    /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i
  return emailRegex.test(email)
}

export function verifyUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

export const PublicPagePaths = [
  'input-output-to-dwl',
  'dwl-sample-data-generator',
  'flow-doc-generator',
  'master-license-agreement',
  'test-drive',
  'privacy',
  'terms',
  'agent',
  'mt-to-dwl',
  'signup',
  'signin',
  'login',
  'verify-email',
  'trial-conversion'
]

export const isCampaignUuid = (uuid?: string) => {
  if (uuid == null) return false
  return PublicPagePaths.includes(uuid)
}

export const isPublicPath = () => {
  const { pathname } = new URL(location.href)
  return PublicPagePaths.includes(pathname.replace('/', ''))
}

export const isProtectedPath = () => {
  return !isPublicPath()
}

const protectedRoutes = [
  '/api/user/unlock-account',
  '/api/user/profile',
  '/api/user/delete-account',
  '/api/user/generate-mfa-secret',
  '/api/user/verify-mfa-code',
  '/api/user/sessions',
  '/api/user/access-logs',
  '/api/org/users',
  '/api/billing-accounts/workspaces/create',
  '/api/org/switch-org',
  '/api/conversation/flow-names',
  '/api/conversation/flow-names-test-drive',
  '/api/conversations/',
  '/api/conversations/paginated',
  '/api/kb/annotation',
  '/api/super-admin/csv/signups',
  '/api/super-admin/csv/last-active-users',
  '/api/super-admin/csv/user-activities',
  '/api/super-admin/lookup/email-access-logs',
  '/api/super-admin/lookup/uuid-user',
  '/api/super-admin/settings/campaigns-agent-type',
  '/api/super-admin/billing-list',
  '/api/super-admin/billing/update-billing-type',
  '/api/super-admin/tasks/domains',
  '/api/super-admin/tasks/emails',
  '/api/super-admin/tasks/workspaces',
  '/api/super-admin/heartbeat'
]

const protectRouteSchemas = [
  '/api/workspace/{workspace_uuid}/indexes/{index_id}/update',
  '/api/workspace/{workspace_uuid}/indexes/{index_id}/update-runtime-setting',
  '/api/conversation/{conversation_uuid}',
  '/api/conversation/{conversation_uuid}/title',
  '/api/conversation/{conversation_uuid}/generate-title',
  '/api/conversation/{conversation_uuid}/metadata',
  '/api/conversation/{conversation_uuid}/chat',
  // Workspace-aware routes (org-related)
  '/api/workspace/{workspace_uuid}/org/update-org-name',
  '/api/workspace/{workspace_uuid}/org/update-org-sso-clientID',
  '/api/workspace/{workspace_uuid}/org/update-org-sso-client-secret',
  '/api/workspace/{workspace_uuid}/org/update-org-sso-tenant-id',
  '/api/workspace/{workspace_uuid}/org/update-org-sso-call-back-url',
  '/api/workspace/{workspace_uuid}/org/set-timeout',
  '/api/workspace/{workspace_uuid}/org/org-repos',
  '/api/workspace/{workspace_uuid}/org/delete-org',
  '/api/workspace/{workspace_uuid}/org/update-org-user-role',
  '/api/workspace/{workspace_uuid}/org/remove-user',
  '/api/workspace/{workspace_uuid}/org/users',
  '/api/workspace/{workspace_uuid}/org/index',
  // Workspace-aware routes (connection-related)
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/repos',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/branches',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/selected-branches',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/set-access-token',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/get-custom-connection-config',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/validate-token',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/available-organizations',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}/set-organization',
  '/api/workspace/{workspace_uuid}/conn/{conn_type}',
  '/api/workspace/{workspace_uuid}/conn/update-connection',
  '/api/workspace/{workspace_uuid}/conn/get-connection-metadata',
  // Workspace-aware routes (general)
  '/api/workspace/{workspace_uuid}/org/invitation',
  '/api/workspace/{workspace_uuid}/org/coding-guidelines',
  '/api/workspace/{workspace_uuid}/org/update-coding-guidelines-metadata',
  '/api/workspace/{workspace_uuid}/org/repo-cache-ttl',
  '/api/workspace/{workspace_uuid}/org/upload/{client_type}/presigned-url',
  '/api/workspace/{workspace_uuid}/org/upload/{client_type}/complete',
  '/api/workspace/{workspace_uuid}/conversation/task-submit',
  '/api/workspace/{workspace_uuid}/conversation/{conversation_uuid}/task/{task_id}',
  '/api/workspace/{workspace_uuid}/conversation/{conversation_uuid}/task/{task_id}/submit',
  '/api/workspace/{workspace_uuid}/conversation/{conversation_uuid}/task/{task_id}/task-update/{status}'
]

const compiledProtectedRoutes = protectRouteSchemas.map(route => {
  return new RegExp(`^${route.replace(/\{([^}]+)\}/g, '([^/]+)').replace(/\//g, '\\/')}$`)
})

export const isProtectedAPIRoutePath = (url: string) => {
  return protectedRoutes.includes(url) || compiledProtectedRoutes.some(regex => regex.test(url))
}

export const formatFileSize = (size: number): string => {
  if (size < 1024) {
    return `${size} B`
  } else if (size < 1048576) {
    return `${(size / 1024).toFixed(2)} KB`
  } else {
    return `${(size / 1048576).toFixed(2)} MB`
  }
}

export const isValidString = (str: string | undefined | null) => {
  return str && str.trim() !== ''
}

export const isChrome = () => {
  const userAgent = navigator.userAgent.toLowerCase()

  // Check if browser is Chrome but not Edge/Opera/Brave (which also use Chromium)
  const isChromeBrowser =
    /chrome/.test(userAgent) &&
    !/edg/.test(userAgent) && // Not Edge
    !/opr/.test(userAgent) && // Not Opera
    !/brave/.test(userAgent) // Not Brave

  return isChromeBrowser
}

/**
 * Check if the current hostname is the super admin subdomain
 * Matches superadmin.curietech.ai in production or localhost for development
 */
export const isSuperAdminSubdomain = (): boolean => {
  const hostname = window.location.hostname
  return hostname.startsWith('superadmin.')
}

/**
 * Check if the current hostname is a curietech.ai domain but not the superadmin subdomain
 * Used to block /super-admin routes on platform.curietech.ai (and other curietech.ai subdomains)
 */
export const isPlatformDomain = (): boolean => {
  const hostname = window.location.hostname
  return hostname.endsWith('curietech.ai') && !isSuperAdminSubdomain()
}

/**
 * Get the super admin URL based on the current environment
 * In production (curietech.ai), returns the superadmin subdomain URL
 * In development/other environments, returns the /super-admin path
 */
export const getSuperAdminUrl = (): string => {
  const hostname = window.location.hostname
  // Production: curietech.ai or any subdomain of curietech.ai
  if (hostname.endsWith('curietech.ai')) {
    return 'https://superadmin.curietech.ai'
  }
  // Development/staging: use the path-based route
  return '/super-admin'
}

/**
 * Get the main app URL from super admin based on the current environment
 * In production (superadmin.curietech.ai), returns the platform subdomain URL
 * In development/other environments, returns the root path
 */
export const getMainAppUrl = (): string => {
  const hostname = window.location.hostname
  // Production: superadmin.curietech.ai
  if (hostname === 'superadmin.curietech.ai') {
    return 'https://platform.curietech.ai'
  }
  // Development/staging: return to root
  return '/'
}
